import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Building2, Loader2, Pencil, Trash2, X, Save, Globe2, Target, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";

const FRANCE_CENTER = [46.6, 2.5];
const DEPTS_GEOJSON = "https://cdn.jsdelivr.net/gh/gregoiredavid/france-geojson@master/departements-version-simplifiee.geojson";

const STATUS_META = {
  ACTIVE: { label: "Actif", pin: "#0066DB", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PENDING_VERIFICATION: { label: "Vérification", pin: "#F59E0B", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  PENDING_SIGNATURE: { label: "Signature", pin: "#F59E0B", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  DRAFT: { label: "Brouillon", pin: "#9CA3AF", badge: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  SUSPENDED: { label: "Suspendu", pin: "#F97316", badge: "bg-orange-50 text-orange-700 border-orange-200" },
  TERMINATED: { label: "Résilié", pin: "#EF4444", badge: "bg-red-50 text-red-700 border-red-200" },
};
const metaFor = (st) => STATUS_META[st] || STATUS_META.ACTIVE;
const STATUS_OPTIONS = Object.keys(STATUS_META);

function pinIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });
}

// ------- Petite fenêtre de confirmation (obligatoire avant suppression) -------
function ConfirmDialog({ open, title, message, confirmLabel = "Supprimer", onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4" data-testid="confirm-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-50 text-red-600"><Trash2 className="h-5 w-5" /></div>
          <div>
            <h3 className="text-lg font-black text-neutral-900">{title}</h3>
            <p className="mt-1 text-sm text-neutral-600">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel-btn"
            className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Annuler</button>
          <button type="button" onClick={onConfirm} disabled={busy} data-testid="confirm-ok-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- Fenêtre d'édition d'une auto-école -------
function EditSchoolModal({ school, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: school.name || "", email: school.email || "", phone: school.phone || "",
    address: school.address || "", city: school.city || "", postal_code: school.postal_code || "",
    status: school.status || "ACTIVE",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/schools/${school.id}`, form);
      toast.success("Auto-école mise à jour.");
      onSaved();
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4" data-testid="edit-school-modal">
      <form onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-black text-neutral-900">Modifier l’auto-école</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs font-bold uppercase tracking-wide text-neutral-400">Nom
            <input value={form.name} onChange={set("name")} data-testid="edit-name-input"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900" /></label>
          <label className="text-xs font-bold uppercase tracking-wide text-neutral-400">Email
            <input value={form.email} onChange={set("email")} data-testid="edit-email-input"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900" /></label>
          <label className="text-xs font-bold uppercase tracking-wide text-neutral-400">Téléphone
            <input value={form.phone} onChange={set("phone")} data-testid="edit-phone-input"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900" /></label>
          <label className="col-span-2 text-xs font-bold uppercase tracking-wide text-neutral-400">Adresse
            <input value={form.address} onChange={set("address")} data-testid="edit-address-input"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900" /></label>
          <label className="text-xs font-bold uppercase tracking-wide text-neutral-400">Code postal
            <input value={form.postal_code} onChange={set("postal_code")} data-testid="edit-postal-input"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900" /></label>
          <label className="text-xs font-bold uppercase tracking-wide text-neutral-400">Ville
            <input value={form.city} onChange={set("city")} data-testid="edit-city-input"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900" /></label>
          <label className="col-span-2 text-xs font-bold uppercase tracking-wide text-neutral-400">Statut
            <select value={form.status} onChange={set("status")} data-testid="edit-status-select"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-normal normal-case text-neutral-900">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{metaFor(s).label}</option>)}
            </select></label>
        </div>
        <p className="mt-2 text-xs text-neutral-500">Modifier l’adresse recalcule automatiquement la position sur la carte.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Annuler</button>
          <button type="submit" disabled={saving} data-testid="edit-save-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminSchoolsMap() {
  const [data, setData] = useState({ schools: [], total: 0, total_all: 0, located: 0, cities: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("schools"); // "schools" | "zones"

  // Zones
  const [zoneMode, setZoneMode] = useState("all");
  const [openDeps, setOpenDeps] = useState(() => new Set());
  const [savingZones, setSavingZones] = useState(false);
  const zoneRef = useRef({ mode: "all", deps: new Set() });

  // Sélection / édition / confirmation
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState({ open: false });
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [geo, setGeo] = useState(null);

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef({});
  const geoLayer = useRef(null);

  const loadSchools = useCallback(() => {
    setLoading(true);
    return api.get("/admin/schools/map")
      .then(({ data }) => setData(data || { schools: [] }))
      .catch(() => toast.error("Impossible de charger les auto-écoles."))
      .finally(() => setLoading(false));
  }, []);

  const loadZones = useCallback(() => {
    return api.get("/admin/zones")
      .then(({ data }) => {
        setZoneMode(data.zones_mode || "all");
        setOpenDeps(new Set(data.open_departments || []));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadSchools(); loadZones(); }, [loadSchools, loadZones]);

  // GeoJSON des départements (une seule fois)
  useEffect(() => {
    let alive = true;
    fetch(DEPTS_GEOJSON)
      .then((r) => r.json())
      .then((j) => { if (alive) setGeo(j); })
      .catch(() => { /* la carte fonctionne sans les polygones */ });
    return () => { alive = false; };
  }, []);

  // garder la dernière valeur des zones pour les handlers de clic Leaflet
  useEffect(() => { zoneRef.current = { mode: zoneMode, deps: openDeps }; }, [zoneMode, openDeps]);

  const toggleDept = useCallback((code) => {
    setZoneMode((prevMode) => {
      setOpenDeps((prev) => {
        const next = new Set(prevMode === "all" ? [] : prev);
        if (next.has(code)) next.delete(code); else next.add(code);
        return next;
      });
      return "regions"; // dès qu'on clique une zone, on passe en mode ciblé
    });
  }, []);

  // Init carte (une seule fois)
  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: true }).setView(FRANCE_CENTER, 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap" }).addTo(map);
    mapObj.current = map;
  }, []);

  // Couche départements : construite quand le GeoJSON arrive
  useEffect(() => {
    const map = mapObj.current;
    if (!map || !geo) return;
    if (geoLayer.current) { map.removeLayer(geoLayer.current); geoLayer.current = null; }
    const layer = L.geoJSON(geo, {
      style: () => ({ color: "#CBD5E1", weight: 1, fillColor: "#E2E8F0", fillOpacity: 0.08 }),
      onEachFeature: (feature, lyr) => {
        const code = feature?.properties?.code;
        const nom = feature?.properties?.nom || "";
        lyr._deptCode = code;
        lyr.bindTooltip(`${nom} (${code})`, { sticky: true });
        lyr.on("click", () => code && toggleDept(code));
      },
    }).addTo(map);
    geoLayer.current = layer;
  }, [geo, toggleDept]);

  // (Re)coloration des départements selon l'état des zones
  useEffect(() => {
    const layer = geoLayer.current;
    if (!layer) return;
    layer.eachLayer((lyr) => {
      const code = lyr._deptCode;
      const open = zoneMode === "all" ? true : openDeps.has(code);
      lyr.setStyle({
        color: open ? "#0066DB" : "#CBD5E1",
        weight: open ? 1.4 : 1,
        fillColor: open ? "#0066DB" : "#E2E8F0",
        fillOpacity: open ? 0.22 : 0.06,
      });
    });
  }, [zoneMode, openDeps, geo]);

  // Marqueurs auto-écoles
  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    Object.values(markers.current).forEach((m) => map.removeLayer(m));
    markers.current = {};
    const located = data.schools.filter((s) => s.lat != null && s.lng != null);
    located.forEach((s) => {
      const m = L.marker([s.lat, s.lng], { icon: pinIcon(metaFor(s.status).pin) }).addTo(map);
      m.bindPopup(
        `<strong>${s.name}</strong><br/>${s.address || ""} ${s.postal_code || ""} ${s.city || ""}<br/>` +
        `<span style="color:${metaFor(s.status).pin};font-weight:700">${metaFor(s.status).label}</span> · ${s.open_places} place(s) · ${s.reservations} réservation(s)`
      );
      markers.current[s.id] = m;
    });
    if (located.length && geoLayer.current == null) {
      const group = L.featureGroup(Object.values(markers.current));
      try { map.fitBounds(group.getBounds().pad(0.25)); } catch (e) { /* no-op */ }
    }
  }, [data]);

  const focus = (s) => {
    const map = mapObj.current;
    if (!map || s.lat == null) return;
    map.setView([s.lat, s.lng], 13);
    markers.current[s.id]?.openPopup();
  };

  // ------- Sélection multiple -------
  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const allSelected = data.schools.length > 0 && data.schools.every((s) => selected.has(s.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(data.schools.map((s) => s.id)));

  // ------- Suppressions -------
  const askDeleteOne = (s) => setConfirm({
    open: true, title: "Supprimer cette auto-école ?",
    message: `« ${s.name} » sera définitivement supprimée de la base (dispos, formules et compte liés). Cette action est irréversible.`,
    onConfirm: async () => {
      setConfirmBusy(true);
      try {
        await api.delete(`/admin/schools/${s.id}`);
        toast.success("Auto-école supprimée.");
        setSelected((p) => { const n = new Set(p); n.delete(s.id); return n; });
        setConfirm({ open: false });
        await loadSchools();
      } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
      finally { setConfirmBusy(false); }
    },
  });

  const askDeleteSelection = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setConfirm({
      open: true, title: `Supprimer ${ids.length} auto-école(s) ?`,
      message: "Les auto-écoles sélectionnées seront définitivement supprimées de la base. Cette action est irréversible.",
      onConfirm: async () => {
        setConfirmBusy(true);
        try {
          const { data: res } = await api.post("/admin/schools/bulk-delete", { ids });
          toast.success(`${res.count} auto-école(s) supprimée(s).`);
          setSelected(new Set());
          setConfirm({ open: false });
          await loadSchools();
        } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
        finally { setConfirmBusy(false); }
      },
    });
  };

  // ------- Enregistrement des zones -------
  const saveZones = async () => {
    setSavingZones(true);
    try {
      await api.put("/admin/zones", {
        zones_mode: zoneMode,
        open_departments: zoneMode === "regions" ? [...openDeps] : [],
      });
      toast.success("Zones d'attaque enregistrées.");
      await loadZones();
    } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
    finally { setSavingZones(false); }
  };

  const openDeptCount = zoneMode === "all" ? "Tous" : openDeps.size;

  return (
    <Shell role="admin">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-neutral-900">Carte des auto-écoles & zones</h1>
          <p className="mt-1 text-sm text-neutral-500">Visualise tes auto-écoles partenaires et tes zones d’attaque. Modifie ou supprime directement depuis la liste.</p>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="map-stats">
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-brand"><Building2 className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Partenaires actifs</span></div>
          <p className="mt-1 text-2xl font-black text-neutral-900">{data.total}</p>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-brand"><Building2 className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Total écoles</span></div>
          <p className="mt-1 text-2xl font-black text-neutral-900">{data.total_all}</p>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-brand"><MapPin className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Localisées</span></div>
          <p className="mt-1 text-2xl font-black text-neutral-900">{data.located}</p>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-brand"><Target className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Départements ouverts</span></div>
          <p className="mt-1 text-2xl font-black text-neutral-900">{openDeptCount}</p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="relative overflow-hidden rounded-2xl border border-neutral-200 shadow-sm" style={{ minHeight: 560 }}>
          <div ref={mapRef} data-testid="admin-map" style={{ height: 560, width: "100%", zIndex: 0 }} />
          {loading && (
            <div className="absolute inset-0 grid place-items-center bg-white/70">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          )}
          <div className="pointer-events-none absolute bottom-3 left-3 z-[400] rounded-xl bg-white/90 px-3 py-2 text-[11px] font-semibold text-neutral-600 shadow">
            <div className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "#0066DB", opacity: 0.4 }} /> Zone ouverte</div>
            <div className="mt-1 flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#0066DB" }} /> École active</div>
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <div className="flex border-b border-neutral-200">
            <button type="button" onClick={() => setTab("schools")} data-testid="tab-schools"
              className={`flex-1 px-4 py-3 text-sm font-bold ${tab === "schools" ? "border-b-2 border-brand text-brand" : "text-neutral-500"}`}>
              Auto-écoles ({data.schools.length})
            </button>
            <button type="button" onClick={() => setTab("zones")} data-testid="tab-zones"
              className={`flex-1 px-4 py-3 text-sm font-bold ${tab === "zones" ? "border-b-2 border-brand text-brand" : "text-neutral-500"}`}>
              Zones d’attaque
            </button>
          </div>

          {tab === "schools" ? (
            <div className="p-4" data-testid="map-school-list">
              <div className="mb-3 flex items-center justify-between gap-2">
                <button type="button" onClick={toggleSelectAll} data-testid="select-all-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-neutral-500 hover:text-brand">
                  {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />} Tout sélectionner
                </button>
                <button type="button" onClick={askDeleteSelection} disabled={selected.size === 0} data-testid="bulk-delete-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-40">
                  <Trash2 className="h-4 w-4" /> Supprimer ({selected.size})
                </button>
              </div>
              <div className="max-h-[560px] space-y-2 overflow-auto">
                {data.schools.length === 0 && !loading && (
                  <p className="text-sm text-neutral-500">Aucune auto-école pour le moment.</p>
                )}
                {data.schools.map((s) => {
                  const m = metaFor(s.status);
                  const sel = selected.has(s.id);
                  return (
                    <div key={s.id} data-testid={`map-school-${s.id}`}
                      className={`rounded-xl border p-3 transition-colors ${sel ? "border-brand bg-brand/5" : "border-neutral-200"}`}>
                      <div className="flex items-start gap-2">
                        <button type="button" onClick={() => toggleSelect(s.id)} data-testid={`select-${s.id}`} className="mt-0.5 text-neutral-400 hover:text-brand">
                          {sel ? <CheckSquare className="h-4 w-4 text-brand" /> : <Square className="h-4 w-4" />}
                        </button>
                        <button type="button" onClick={() => focus(s)} className="min-w-0 flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-bold text-neutral-900">{s.name}</span>
                            {s.lat == null && <span className="shrink-0 text-[11px] font-semibold text-amber-600">non localisée</span>}
                          </div>
                          <div className="mt-0.5 text-xs text-neutral-500">{[s.postal_code, s.city].filter(Boolean).join(" ") || "Adresse manquante"}</div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                            <span className={`rounded-full border px-2 py-0.5 font-bold ${m.badge}`}>{m.label}</span>
                            <span className="text-neutral-500">{s.open_places} place(s) · {s.reservations} réserv.</span>
                          </div>
                        </button>
                        <div className="flex shrink-0 flex-col gap-1">
                          <button type="button" onClick={() => setEditing(s)} data-testid={`edit-${s.id}`}
                            className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-brand" title="Modifier"><Pencil className="h-4 w-4" /></button>
                          <button type="button" onClick={() => askDeleteOne(s)} data-testid={`delete-${s.id}`}
                            className="rounded-lg p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-4" data-testid="zones-panel">
              <p className="text-sm text-neutral-600">Choisis où PlanPermis est présent. Clique sur un département de la carte pour l’ouvrir ou le fermer.</p>
              <div className="mt-4 space-y-2">
                <button type="button" onClick={() => setZoneMode("all")} data-testid="zone-mode-all"
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${zoneMode === "all" ? "border-brand bg-brand/5" : "border-neutral-200"}`}>
                  <Globe2 className={`h-5 w-5 ${zoneMode === "all" ? "text-brand" : "text-neutral-400"}`} />
                  <span><span className="block text-sm font-bold text-neutral-900">Tout le pays</span><span className="block text-xs text-neutral-500">Les leads sont acceptés partout en France.</span></span>
                </button>
                <button type="button" onClick={() => setZoneMode("regions")} data-testid="zone-mode-regions"
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${zoneMode === "regions" ? "border-brand bg-brand/5" : "border-neutral-200"}`}>
                  <Target className={`h-5 w-5 ${zoneMode === "regions" ? "text-brand" : "text-neutral-400"}`} />
                  <span><span className="block text-sm font-bold text-neutral-900">Zones ciblées</span><span className="block text-xs text-neutral-500">Seuls les départements ouverts (en bleu) acceptent des leads.</span></span>
                </button>
              </div>

              {zoneMode === "regions" && (
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">Départements ouverts ({openDeps.size})</span>
                    {openDeps.size > 0 && (
                      <button type="button" onClick={() => setOpenDeps(new Set())} className="text-xs font-semibold text-neutral-400 hover:text-red-600">Tout effacer</button>
                    )}
                  </div>
                  <div className="flex max-h-40 flex-wrap gap-1.5 overflow-auto">
                    {[...openDeps].sort().map((d) => (
                      <button key={d} type="button" onClick={() => toggleDept(d)}
                        className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-bold text-brand hover:bg-brand/20">
                        {d} <X className="h-3 w-3" />
                      </button>
                    ))}
                    {openDeps.size === 0 && <span className="text-xs text-neutral-400">Aucun département ouvert — clique sur la carte.</span>}
                  </div>
                </div>
              )}

              <button type="button" onClick={saveZones} disabled={savingZones} data-testid="save-zones-btn"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60">
                {savingZones ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer les zones
              </button>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditSchoolModal school={editing} onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await loadSchools(); }} />
      )}
      <ConfirmDialog open={confirm.open} title={confirm.title} message={confirm.message}
        busy={confirmBusy} onConfirm={confirm.onConfirm} onCancel={() => setConfirm({ open: false })} />
    </Shell>
  );
}
